class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  async renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    // Punch-list fix (M15): the /cart/add.js response is expected to carry a
    // `sections` map (requested via the `sections` form field in
    // product-form.js). When that map is missing or incomplete — e.g. a
    // request that raced a page navigation, or a proxy/cache that stripped
    // it — the old code did `parsedState.sections[section.id]` straight away
    // and threw "Cannot read properties of undefined (reading
    // 'cart-notification-product')", which aborted the whole render: no
    // image, no subtotal, no live item count, even though the item really
    // was added to the cart. Guard it, and fall back to re-rendering the
    // sections manually via the Section Rendering API so the notification
    // still fills in correctly.
    let sections = parsedState && parsedState.sections;

    if (!sections) {
      sections = await this.fetchSectionsFallback();
    }

    if (!sections) {
      console.warn(
        'cart-notification: no section data on add-to-cart response; showing item count only.'
      );
      this.updateCartIconBubbleOnly();
      if (this.header) this.header.reveal();
      this.open();
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = sections[section.id];
      if (!target || !html) return;
      target.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  // Fallback used only when the add-to-cart response didn't include
  // `sections` (see renderContents above). Re-requests the same three
  // sections directly from the Section Rendering API so the notification can
  // still render the real product image, title and subtotal instead of
  // silently staying empty.
  async fetchSectionsFallback() {
    const ids = this.getSectionsToRender().map((section) => section.id);
    return await this.fetchSectionsIndividually(ids);
  }

  async fetchSectionsIndividually(ids) {
    const entries = await Promise.all(
      ids.map(async (id) => {
        try {
          const res = await fetch(`${routes.cart_url}?section_id=${id}`);
          if (!res.ok) return [id, null];
          const html = await res.text();
          return [id, html];
        } catch (e) {
          return [id, null];
        }
      })
    );
    const sections = {};
    let any = false;
    entries.forEach(([id, html]) => {
      if (html) {
        sections[id] = html;
        any = true;
      }
    });
    return any ? sections : null;
  }

  // Last-resort fallback: at minimum keep the cart icon's item count correct
  // and open the notification, rather than leaving the UI in a half-updated
  // state after a failed render.
  async updateCartIconBubbleOnly() {
    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
      if (bubble && typeof cart.item_count === 'number') {
        bubble.textContent = cart.item_count;
      }
    } catch (e) {
      // best-effort only
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const match = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return match ? match.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
